import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, FileText, TrendingUp, Clock, LogOut, PlusCircle, BarChart3, Shield, CheckCircle2, Lock, FileSearch, ClipboardCheck, Building2, Receipt, User, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../lib/BrandingContext';
import ModuleWalletDropdown from '../components/ModuleWalletDropdown';
import BuyCreditsModal from '../components/BuyCreditsModal';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [featureToggles, setFeatureToggles] = useState({
    create_legal_opinion: true,
    ec_extractor: true,
    institution_checklist: true
  });
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats();
    fetchFeatureToggles();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`);
      setFeatureToggles(response.data.toggles);
    } catch (err) {
      console.error('Error fetching feature toggles:', err);
      // Keep default feature toggles if API fails
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleStartNew = () => {
    // Navigate directly to extract page (removed institution checklist)
    navigate('/extract');
  };

  const handleInstitutionChecklist = () => {
    // Direct navigation to Institution Checklist
    navigate('/institution-checklist');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={branding.company_name || 'Logo'} 
                  className="h-10 w-auto"
                />
              ) : (
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `var(--primary-color, #2563eb)` }}
                >
                  <Scale className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {branding.company_name || 'LegalOpinion AI'}
                </h1>
                <p className="text-sm text-gray-500">Professional Document Automation</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {user?.role === 'super_admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center space-x-2 px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </button>
              )}
              
              {/* Review Button for Reviewers and Tenant Admins */}
              {(user?.role === 'reviewer' || user?.role === 'tenant_admin') && (
                <button
                  onClick={() => navigate('/review')}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Review</span>
                </button>
              )}
              
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                {user?.role && (
                  <p className="text-xs text-blue-600 font-medium capitalize">{user.role.replace('_', ' ')}</p>
                )}
              </div>
              
              {/* Wallet Dropdown */}
              <ModuleWalletDropdown />
              
              <button
                onClick={() => navigate('/password-management')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Password Management"
              >
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user?.full_name?.split(' ')[0]}!</h2>
          <p className="text-gray-600">Here's what's happening with your legal documents today.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Documents</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_documents || 0}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Uploaded documents</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Extractions</p>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_extractions || 0}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">AI data extractions</p>
            </div>
          </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Opinions Generated</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_opinions || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <BarChart3 className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">Generated this month</p>
          </div>
        </div>

        {/* My Account Section - Quick Access */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-gray-600" />
            My Account
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/tenant-profile')}
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors"
            >
              <Building2 className="w-8 h-8 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Tenant Profile</span>
              <span className="text-xs text-gray-500">View all details</span>
            </button>
            <button
              onClick={() => navigate('/settings/billing-profile')}
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors"
            >
              <Receipt className="w-8 h-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Billing & GST</span>
              <span className="text-xs text-gray-500">Invoice details</span>
            </button>
            <button
              onClick={() => navigate('/settings/advocate-profile')}
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
            >
              <Scale className="w-8 h-8 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">Advocate Profile</span>
              <span className="text-xs text-gray-500">Document headers</span>
            </button>
            <button
              onClick={() => navigate('/invoices')}
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors"
            >
              <FileText className="w-8 h-8 text-amber-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">My Invoices</span>
              <span className="text-xs text-gray-500">Download GST invoices</span>
            </button>
          </div>
        </div>

        {/* Reorganized Layout: Setup on Left, 5 Action Cards on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          
          {/* LEFT COLUMN - Setup & Configuration (Takes 1 column) */}
          <div className="space-y-6">
            {/* Institution Checklist - Moved here from right side */}
            {featureToggles.institution_checklist && (
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-6 h-6" />
                  <h3 className="text-lg font-bold">Institution Checklist</h3>
                </div>
                <p className="text-amber-100 text-sm mb-4">Verify from official portals</p>
                <button
                  onClick={handleInstitutionChecklist}
                  className="w-full bg-white text-amber-600 px-4 py-2 rounded-lg font-semibold hover:bg-amber-50 transition-colors text-sm inline-flex items-center justify-center space-x-2"
                >
                  <Shield className="w-4 h-4" />
                  <span>Run Check</span>
                </button>
              </div>
            )}
          </div>

          {/* RIGHT SIDE - Action Cards with Feature Toggles (Takes 2 columns, displayed in grid) */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* 1. Create Legal Opinions */}
            {featureToggles.create_legal_opinion && (
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center space-x-2 mb-2">
                  <Scale className="w-6 h-6" />
                  <h3 className="text-lg font-bold">Create Legal Opinion</h3>
                </div>
                <p className="text-blue-100 text-sm mb-4">Generate legal opinions with Retail/APF format selection</p>
                <button
                  onClick={handleStartNew}
                  className="w-full bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-sm inline-flex items-center justify-center space-x-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>Start New</span>
                </button>
              </div>
            )}

            {/* 2. EC Extractor */}
            {featureToggles.ec_extractor && (
              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center space-x-2 mb-2">
                  <FileSearch className="w-6 h-6" />
                  <h3 className="text-lg font-bold">EC Extractor</h3>
                </div>
                <p className="text-green-100 text-sm mb-4">Extract EC certificate data</p>
                <button
                  onClick={() => navigate('/ec-extractor')}
                  className="w-full bg-white text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors text-sm inline-flex items-center justify-center space-x-2"
                >
                  <FileSearch className="w-4 h-4" />
                  <span>Extract EC</span>
                </button>
              </div>
            )}

            {/* 4. View My Opinions - Moved next to EC Extractor */}
            {featureToggles.ec_extractor && (
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center space-x-2 mb-2">
                  <BarChart3 className="w-6 h-6" />
                  <h3 className="text-lg font-bold">View My Opinions</h3>
                </div>
                <p className="text-teal-100 text-sm mb-4">Browse all generated legal opinions</p>
                <button
                  onClick={() => navigate('/my-opinions')}
                  className="w-full bg-white text-teal-600 px-4 py-2 rounded-lg font-semibold hover:bg-teal-50 transition-colors text-sm inline-flex items-center justify-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>View All Opinions</span>
                </button>
              </div>
            )}

            {/* 5. Missing Document Identifier */}
            <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl shadow-lg p-6 text-white">
              <div className="flex items-center space-x-2 mb-2">
                <FileSearch className="w-6 h-6" />
                <h3 className="text-lg font-bold">Missing Doc Identifier</h3>
              </div>
              <p className="text-amber-100 text-sm mb-4">Check document completeness before opinion</p>
              <button
                onClick={() => navigate('/missing-docs')}
                className="w-full bg-white text-amber-600 px-4 py-2 rounded-lg font-semibold hover:bg-amber-50 transition-colors text-sm inline-flex items-center justify-center space-x-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                <span>Check Documents</span>
              </button>
            </div>

          </div>
        </div>

        {/* Additional Cards Below (Admin, etc.) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Review Opinions Card for Reviewers */}
          {(user?.role === 'reviewer' || user?.role === 'tenant_admin') && (
            <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl shadow-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-3">Review Opinions</h3>
              <p className="text-orange-100 mb-6">Review and approve legal opinions submitted by advocates. Add comments and provide feedback.</p>
              
              <button
                onClick={() => navigate('/review')}
                className="w-full flex items-center justify-center space-x-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors mb-4"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>Go to Review Dashboard</span>
              </button>

              <div className="pt-4 border-t border-orange-500 border-opacity-30">
                <p className="text-orange-100 text-sm mb-3">Review workflow:</p>
                <ul className="text-orange-100 text-sm space-y-1">
                  <li>• Download DOCX for offline review</li>
                  <li>• Add multiple comments</li>
                  <li>• Approve or reject with feedback</li>
                </ul>
              </div>
            </div>
          )}

          {/* Format Distribution */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Format Distribution</h3>
            {stats?.format_distribution && stats.format_distribution.length > 0 ? (
              <div className="space-y-3">
                {stats.format_distribution.map((format) => (
                  <div key={format._id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">{format._id || 'Unknown'}</span>
                    <div className="flex items-center space-x-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(format.count / stats.total_opinions) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">{format.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No opinions generated yet</p>
            )}
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-gray-600" />
              Recent Documents
            </h3>
          </div>
          <div className="p-6">
            {stats?.recent_documents && stats.recent_documents.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 capitalize">{doc.mime_type.split('/')[1]}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No documents uploaded yet</p>
                <button
                  onClick={handleStartNew}
                  className="mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  Upload your first document →
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
};

export default DashboardPage;
